import type {IServiceDescription, VatRate} from "@mytour/shared";
import {
  MAX_ONLINE_DURATION_MINUTES,
  NullableVatRateSchema,
  RDV_ONLINE_DURATION_REQUIRED_MESSAGE,
  ServiceType,
  VAT_RATES,
} from "@mytour/shared";
import {FloatingField, FloatingUnitField} from "@mytour/ui/form";
import {Splitter} from "@mytour/ui/layout";
import type {Control, FieldErrors, UseFormSetValue} from "react-hook-form";
import {Controller, useWatch} from "react-hook-form";
import {useTranslation} from "react-i18next";
import {Button, FormFeedback, FormGroup, Input, Label} from "reactstrap";
import {z} from "zod";

/** Quick-pick durations (minutes) — "liste rapide" alongside free entry (FR9). */
const DURATION_PRESETS = [15, 30, 45, 60] as const;

/**
 * Duration pre-filled when the pro switches a prestation to online-bookable while
 * the field is still empty (#673) — a sensible default so the mandatory duration
 * isn't left empty (and invalid) the moment booking is enabled. Intentionally one
 * of the `DURATION_PRESETS` above (30) so the pre-filled value is also a
 * quick-pick; kept as an explicit literal (not `DURATION_PRESETS[1]`) for
 * readability — if the presets change, revisit this default.
 */
const DEFAULT_ONLINE_DURATION_MINUTES = 30;

export const serviceDescriptionSchema = z
  .object({
    label: z.string().min(1, "entity.validation.required"),
    amount: z.number().nonnegative("entity.validation.number"),
    type: z.nativeEnum(ServiceType),
    account: z.number().max(999999, "entity.validation.number").optional(),
    vatRate: NullableVatRateSchema,
    // RDV en ligne (#429, Q8 / FR9-FR11). The duration carries NO field-level
    // numeric constraint: all checks are gated on the toggle below. Otherwise a
    // stale out-of-range value typed while online would block submit after the
    // user disables booking — even though the value is nulled on save.
    onlineBookingEnabled: z.boolean(),
    onlineDurationMinutes: z.number().nullable(),
  })
  .superRefine((value, ctx) => {
    if (!value.onlineBookingEnabled) {
      return; // Off: the duration is irrelevant (nulled on save) — no constraint.
    }
    const duration = value.onlineDurationMinutes;
    // FR11: online ⇒ a positive integer duration is mandatory.
    if (duration == null || !Number.isInteger(duration) || duration <= 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["onlineDurationMinutes"],
        message: RDV_ONLINE_DURATION_REQUIRED_MESSAGE,
      });
      return;
    }
    if (duration > MAX_ONLINE_DURATION_MINUTES) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["onlineDurationMinutes"],
        message: `La durée ne peut pas dépasser ${MAX_ONLINE_DURATION_MINUTES} minutes`,
      });
    }
  });

export type ServiceDescriptionFormData = z.infer<typeof serviceDescriptionSchema>;

type Props = {
  control: Control<ServiceDescriptionFormData>;
  errors: FieldErrors<ServiceDescriptionFormData>;
  /** From the parent's `useForm` — used to pre-fill the duration on toggle (#673). */
  setValue: UseFormSetValue<ServiceDescriptionFormData>;
  disabled: boolean;
  hasVatEnabled: boolean;
  /**
   * RDV en ligne (#429, #559). Gates the online-booking UX behind the
   * `rdv-en-ligne` feature flag (read by the parent modal, passed as a
   * primitive so this component stays pure). Off → the toggle, duration presets
   * and duration field are hidden; the form still submits `onlineBookingEnabled:
   * false` (schema default), so no online prestation is created while masked.
   */
  showOnlineBooking: boolean;
};

export function ServiceDescriptionForm({
  control,
  errors,
  setValue,
  disabled,
  hasVatEnabled,
  showOnlineBooking,
}: Props) {
  const {t: translate} = useTranslation();
  // RDV en ligne (#429, Q8). Drive the duration field's enabled state off the
  // toggle so the duration is grayed out while the prestation is not online.
  const onlineBookingEnabled = useWatch({control, name: "onlineBookingEnabled"});
  const onlineDurationMinutes = useWatch({control, name: "onlineDurationMinutes"});
  const isDurationDisabled = disabled || !onlineBookingEnabled;

  return (
    <>
      <FloatingField<ServiceDescriptionFormData>
        name="label"
        label={translate("myTourApp.serviceDescription.label")}
        control={control}
        error={errors.label}
        disabled={disabled}
        required
      />
      {hasVatEnabled ? (
        <Splitter>
          <div>
            <FloatingUnitField<ServiceDescriptionFormData>
              name="amount"
              label={translate("myTourApp.serviceDescription.amount")}
              suffix="€"
              control={control}
              disabled={disabled}
              step="0.01"
              min={0}
              required
            />
          </div>
          <FloatingField<ServiceDescriptionFormData>
            name="account"
            label="Code compte"
            control={control}
            error={errors.account}
            disabled={disabled}
            type="number"
          />
        </Splitter>
      ) : (
        <FloatingUnitField<ServiceDescriptionFormData>
          name="amount"
          label={translate("myTourApp.serviceDescription.amount")}
          suffix="€"
          control={control}
          disabled={disabled}
          step="0.01"
          min={0}
          required
        />
      )}

      {hasVatEnabled && (
        <FormGroup floating>
          <Controller
            name="vatRate"
            control={control}
            render={({field}) => (
              <Input
                type="select"
                id="field-vatRate"
                disabled={disabled}
                placeholder="Taux de TVA"
                value={field.value ?? ""}
                onChange={(e) =>
                  field.onChange(e.target.value === "" ? null : Number(e.target.value))
                }
                onBlur={field.onBlur}
                name={field.name}
                innerRef={field.ref}
              >
                <option value="20">20% (taux normal)</option>
                <option value="10">10% (taux intermédiaire)</option>
                <option value="5.5">5,5% (taux réduit)</option>
                <option value="2.1">2,1% (taux particulier)</option>
              </Input>
            )}
          />
          <Label for="field-vatRate">Taux de TVA</Label>
        </FormGroup>
      )}

      <FormGroup floating>
        <Controller
          name="type"
          control={control}
          render={({field}) => (
            <Input
              {...field}
              type="select"
              id="field-type"
              disabled={disabled}
              placeholder={translate("myTourApp.serviceDescription.type")}
            >
              {Object.values(ServiceType).map((type) => (
                <option value={type} key={type}>
                  {translate(`myTourApp.serviceDescription.type_${type}`)}
                </option>
              ))}
            </Input>
          )}
        />
        <Label for="field-type">{translate("myTourApp.serviceDescription.type")}</Label>
      </FormGroup>

      {/* RDV en ligne (#429, Q8 / FR9-FR11) — gated by the `rdv-en-ligne` flag (#559) */}
      {showOnlineBooking && (
        <>
          <hr />
          <FormGroup switch className="mb-3">
            <Controller
              name="onlineBookingEnabled"
              control={control}
              render={({field}) => (
                <Input
                  type="switch"
                  role="switch"
                  id="field-onlineBookingEnabled"
                  disabled={disabled}
                  checked={field.value}
                  onChange={(e) => {
                    const checked = e.target.checked;
                    field.onChange(checked);
                    // #673: enabling online booking makes the duration mandatory,
                    // so pre-fill a default when it's still empty — otherwise the
                    // field opens empty (and invalid) and the pro has to type it.
                    if (checked && onlineDurationMinutes == null) {
                      setValue("onlineDurationMinutes", DEFAULT_ONLINE_DURATION_MINUTES, {
                        shouldValidate: false,
                      });
                    }
                  }}
                  onBlur={field.onBlur}
                  name={field.name}
                  innerRef={field.ref}
                />
              )}
            />
            <Label for="field-onlineBookingEnabled" check>
              Réservable à la prise de RDV en ligne
            </Label>
          </FormGroup>

          <Controller
            name="onlineDurationMinutes"
            control={control}
            render={({field}) => (
              <>
                <div className="d-flex gap-2 mb-2 flex-wrap">
                  {DURATION_PRESETS.map((minutes) => (
                    <Button
                      key={minutes}
                      type="button"
                      size="sm"
                      outline
                      color="primary"
                      active={field.value === minutes}
                      disabled={isDurationDisabled}
                      onClick={() => field.onChange(minutes)}
                    >
                      {minutes} min
                    </Button>
                  ))}
                </div>
                <FormGroup floating>
                  <Input
                    type="number"
                    id="field-onlineDurationMinutes"
                    // #673: step=1 (not 5) so the native validity check accepts any
                    // integer allowed by the Zod schema (1..MAX). With step=5 the
                    // valid sequence was 1,6,11,… — rejecting the quick-pick values
                    // 15/30/45/60 on manual entry, while the preset buttons (which
                    // call field.onChange directly) bypassed the native check.
                    min={1}
                    max={MAX_ONLINE_DURATION_MINUTES}
                    step={1}
                    placeholder="Durée du rendez-vous (min)"
                    disabled={isDurationDisabled}
                    invalid={!!errors.onlineDurationMinutes}
                    value={field.value ?? ""}
                    onChange={(e) =>
                      field.onChange(e.target.value === "" ? null : Number(e.target.value))
                    }
                    onBlur={field.onBlur}
                    name={field.name}
                    innerRef={field.ref}
                  />
                  <Label for="field-onlineDurationMinutes">Durée du rendez-vous (min)</Label>
                  {errors.onlineDurationMinutes && (
                    <FormFeedback>{errors.onlineDurationMinutes.message}</FormFeedback>
                  )}
                </FormGroup>
              </>
            )}
          />
        </>
      )}
    </>
  );
}

export function getServiceDescriptionDefaultValues(
  serviceDescription: IServiceDescription | undefined,
  hasVatEnabled: boolean
): ServiceDescriptionFormData {
  if (!serviceDescription) {
    return {
      label: "",
      amount: 20,
      type: ServiceType.SERVICE,
      account: undefined,
      vatRate: hasVatEnabled ? 20 : null,
      onlineBookingEnabled: false,
      onlineDurationMinutes: null,
    };
  }

  return {
    label: serviceDescription.label ?? "",
    amount: serviceDescription.amount ?? 0,
    type: serviceDescription.type ?? ServiceType.SERVICE,
    account: serviceDescription.account ?? undefined,
    vatRate: narrowVatRate(serviceDescription.vatRate) ?? (hasVatEnabled ? 20 : null),
    onlineBookingEnabled: serviceDescription.onlineBookingEnabled ?? false,
    onlineDurationMinutes: serviceDescription.onlineDurationMinutes ?? null,
  };
}

function narrowVatRate(value: number | null | undefined): VatRate | null {
  if (value == null) {
    return null;
  }
  return (VAT_RATES as readonly number[]).includes(value) ? (value as VatRate) : null;
}
